/** OTP-required toggle for invites.
 *
 * Default is on — emailed/SMSed invites get a 6-digit OTP the field
 * user enters on /join. Off makes the signed invite URL the only
 * auth factor. Org admins (perm: org.update) toggle this here.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import { describeApiError, isMfaRequired } from "@/lib/apiErrors";
import type { Organization } from "@/lib/types";


export function OtpRequiredCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const org = useQuery({
    queryKey: ["org-me"],
    queryFn: async () => (await api.get<Organization>(ApiPaths.orgMe)).data,
  });

  const toggle = useMutation({
    mutationFn: async (next: boolean) => {
      const res = await api.patch<Organization>(ApiPaths.orgOtp, {
        require_otp_for_invites: next,
      });
      return res.data;
    },
    onSuccess: (next) => {
      qc.setQueryData(["org-me"], next);
    },
  });

  if (org.isLoading) return null;
  if (!org.data) return null;

  const enabled = org.data.require_otp_for_invites;

  return (
    <section className="card space-y-2">
      <header>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("admin.otp.cardTitle")}
        </h2>
        <p className="text-xs text-nb-text-3">{t("admin.otp.cardHint")}</p>
      </header>
      <div className="flex flex-wrap items-baseline gap-3">
        <span className="text-sm text-nb-text">
          <span className="text-nb-text-2">{t("admin.otp.statusLabel")}</span>{" "}
          <span
            className={
              enabled
                ? "font-medium text-cvp-ok"
                : "font-medium text-nb-text-2"
            }
          >
            {enabled
              ? t("admin.otp.statusEnabled")
              : t("admin.otp.statusDisabled")}
          </span>
        </span>
        <button
          type="button"
          className="btn-ghost text-sm"
          onClick={() => toggle.mutate(!enabled)}
          disabled={toggle.isPending}
        >
          {toggle.isPending
            ? t("admin.otp.saving")
            : enabled
              ? t("admin.otp.disableButton")
              : t("admin.otp.enableButton")}
        </button>
      </div>
      {!enabled && (
        <p className="text-xs text-cvp-warn">{t("admin.otp.disabledWarning")}</p>
      )}
      {toggle.isError && (
        <p className="text-xs text-cvp-danger">
          {isMfaRequired(toggle.error)
            ? t("admin.mfaRequiredHint")
            : describeApiError(toggle.error, t("admin.otp.saveErrorGeneric"))}
        </p>
      )}
    </section>
  );
}
